// All HTTP traffic to the FastAPI backend lives here.
//
// Authentication is cookie-based: /auth/login sets an httpOnly cookie
// that the browser attaches automatically to subsequent requests. We
// pass credentials: 'include' on every fetch so the cookie ships even
// on cross-origin requests (Netlify proxies /api to the Droplet so
// prod is same-origin, but local dev goes through the Vite dev proxy).
// No JWT ever touches JavaScript.

const BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = 'GET', body, headers = {}, raw = false } = {}) {
  const finalHeaders = {
    Accept: 'application/json',
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    ...headers,
  };

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: finalHeaders,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });
  } catch (networkErr) {
    throw new ApiError('Network error — check your connection.', { status: 0 });
  }

  if (res.status === 401) {
    // Session expired or absent — bounce to /login unless we're already
    // there. AuthContext also reacts to a 401 from /auth/me on bootstrap.
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError('Session expired. Please sign in again.', { status: 401 });
  }

  if (raw) {
    if (!res.ok) {
      throw new ApiError(`Request failed (${res.status})`, { status: res.status });
    }
    return res;
  }

  let payload = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const detail =
      (payload && (payload.detail || payload.message)) ||
      (typeof payload === 'string' ? payload : null) ||
      `Request failed (${res.status})`;
    const userMessage =
      res.status === 403 ? "You don't have permission to do that." : detail;
    throw new ApiError(userMessage, { status: res.status, body: payload });
  }

  return payload;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function login(email, password) {
  // Response body: {role, email, full_name, dashboard}. The token is
  // delivered as an httpOnly cookie the browser attaches on later requests.
  return request('/auth/login', {
    method: 'POST',
    body: { email, password },
  });
}

export async function logout() {
  return request('/auth/logout', { method: 'POST' });
}

export async function getMe() {
  return request('/auth/me');
}

export async function getSubmissions(params = {}) {
  const query = buildQuery(params);
  return request(`/submissions${query}`);
}

export async function getSubmission(id) {
  return request(`/submissions/${encodeURIComponent(id)}`);
}

export async function getSubmissionActivity(id) {
  return request(`/submissions/${encodeURIComponent(id)}/activity`);
}

export async function updateSubmission(id, data) {
  return request(`/submissions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function getStats(params = {}) {
  return request(`/submissions/stats${buildQuery(params)}`);
}

export async function getSurveyScores(params = {}) {
  return request(`/submissions/survey-scores${buildQuery(params)}`);
}

export async function getCesTrend(params = {}) {
  const query = buildQuery(params);
  return request(`/submissions/ces-trend${query}`);
}

// --- Sentiment dashboard (prompt 50) ---
export async function getMentions(params = {}) {
  const query = buildQuery(params);
  return request(`/sentiment/mentions${query}`);
}

export async function getMention(id) {
  return request(`/sentiment/mentions/${encodeURIComponent(id)}`);
}

export async function updateMention(id, data) {
  return request(`/sentiment/mentions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function getSentimentStats(params = {}) {
  const query = buildQuery(params);
  return request(`/sentiment/stats${query}`);
}

export async function getSentimentTrends(params = {}) {
  const query = buildQuery(params);
  return request(`/sentiment/trends${query}`);
}

export async function getMentionActivity(id) {
  return request(`/sentiment/mentions/${encodeURIComponent(id)}/activity`);
}

export async function updateMentionCommandCenter(id, submitted) {
  return updateMention(id, { submitted_to_command_center: submitted });
}

export async function downloadSentimentTagReport(params = {}) {
  await downloadCsv(
    '/reports/sentiment/tags', params,
    `cx_tag_frequency_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadSentimentEscalationReport(params = {}) {
  await downloadCsv(
    '/reports/sentiment/escalations', params,
    `cx_escalation_report_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadSentimentBranchReport(params = {}) {
  await downloadCsv(
    '/reports/sentiment/branches', params,
    `cx_branch_sentiment_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

// PDF variants — same raw-blob download path (downloadCsv is format-agnostic;
// it honors the server's Content-Disposition filename).
export async function downloadSentimentTagPdf(params = {}) {
  await downloadCsv(
    '/reports/sentiment/tags/pdf', params,
    `cx_tag_frequency_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

export async function downloadSentimentEscalationPdf(params = {}) {
  await downloadCsv(
    '/reports/sentiment/escalations/pdf', params,
    `cx_escalation_report_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

export async function downloadSentimentBranchPdf(params = {}) {
  await downloadCsv(
    '/reports/sentiment/branches/pdf', params,
    `cx_branch_sentiment_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

export async function downloadSentimentEvolutionReport(params = {}) {
  await downloadCsv(
    '/reports/sentiment/evolution', params,
    `cx_evolution_mentions_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadSentimentEvolutionPdf(params = {}) {
  await downloadCsv(
    '/reports/sentiment/evolution/pdf', params,
    `cx_evolution_mentions_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

export async function downloadSentimentTrendCsv(params = {}) {
  await downloadCsv(
    '/reports/sentiment/trends', params,
    `cx_sentiment_trend_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadSentimentTrendPdf(params = {}) {
  await downloadCsv(
    '/reports/sentiment/trends/pdf', params,
    `cx_sentiment_trend_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

export async function getSentimentReportConfig() {
  return request('/reports/sentiment/config');
}

// --- Upgrade Weekend web analytics (GA4) -----------------------------------
export async function getAnalyticsConfig() {
  return request('/analytics/config');
}

export async function getAnalyticsDashboard(params = {}) {
  return request(`/analytics/dashboard${buildQuery(params)}`);
}

export async function downloadAnalyticsReport(params = {}) {
  await downloadCsv(
    '/analytics/report', params,
    `cx_upgrade_analytics_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadAnalyticsPdf(params = {}) {
  await downloadCsv(
    '/analytics/report/pdf', params,
    `cx_upgrade_analytics_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

export async function downloadTrafficReportCsv(params = {}) {
  await downloadCsv(
    '/analytics/traffic-report', params,
    `cx_upgrade_traffic_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadTrafficReportPdf(params = {}) {
  await downloadCsv(
    '/analytics/traffic-report/pdf', params,
    `cx_upgrade_traffic_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

// --- Bears sports ticker (dashboard widget) ---
export async function getSportsTicker() {
  return request('/sports/ticker');
}

export async function exportCSV(params = {}) {
  const query = buildQuery(params);
  const res = await request(`/submissions/export${query}`, { raw: true });
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename="?([^"]+)"?/i.exec(cd);
  const filename = m ? m[1] : `cx_submissions_${new Date().toISOString().slice(0, 10)}.csv`;

  await triggerBrowserDownload(blob, filename);
}

export async function exportSelected(ids) {
  if (!Array.isArray(ids) || ids.length === 0) {
    throw new ApiError('No submissions selected.', { status: 400 });
  }
  return exportCSV({ ids: ids.join(',') });
}

export async function downloadExecutiveReport(params = {}) {
  const query = buildQuery(params);
  const res = await request(`/submissions/report/download${query}`, { raw: true });
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename="?([^"]+)"?/i.exec(cd);
  const filename = m ? m[1] : `cx_report_${new Date().toISOString().slice(0, 10)}.docx`;

  await triggerBrowserDownload(blob, filename);
}

async function downloadCsv(path, params, fallbackName) {
  const query = buildQuery(params);
  const res = await request(`${path}${query}`, { raw: true });
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename="?([^"]+)"?/i.exec(cd);
  const filename = m ? m[1] : fallbackName;
  await triggerBrowserDownload(blob, filename);
}

export async function downloadFollowUpReport(params = {}) {
  await downloadCsv(
    '/reports/follow-up',
    params,
    `cx_followup_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadAssociateWorkloadReport(params = {}) {
  await downloadCsv(
    '/reports/associate-workload',
    params,
    `cx_workload_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadAssociateCombinedReport(params = {}) {
  await downloadCsv(
    '/reports/associate-combined',
    params,
    `cx_associate_combined_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadHistoricalAssignmentsReport() {
  await downloadCsv(
    '/reports/historical-assignments',
    {},
    `cx_historical_assignments_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadPageSourceReport(params = {}) {
  await downloadCsv(
    '/reports/page-source',
    params,
    `cx_page_source_report_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadSurveyScoresPdf(params = {}) {
  await downloadCsv(
    '/reports/survey-scores/pdf',
    params,
    `cx_experience_scores_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

async function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildQuery(params) {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const q = usp.toString();
  return q ? `?${q}` : '';
}
